import { BACKOFFICE_BASE } from './backofficeRoutes';

/**
 * RBAC (Role-Based Access Control) core for the Saee admin dashboard.
 *
 * Design goals:
 * - Permission-based rather than role-string checks scattered in JSX.
 * - Easy to extend: add a new Permission value and update the role map.
 * - Maps backend roles to app-level roles so the UI can evolve independently.
 */

/** Normalized app-level roles used by the admin dashboard. */
export type AppRole =
  | 'SYSTEM_ADMIN'
  | 'OPERATIONS_MANAGER'
  | 'FINANCIAL_OFFICER'
  | 'SUPPORT_AGENT';

/** Fine-grained permissions for every admin action / view. */
export type Permission =
  | 'CAN_VIEW_OVERVIEW'
  | 'CAN_VIEW_BROKERS'
  | 'CAN_CREATE_BROKER'
  | 'CAN_EDIT_BROKER'
  | 'CAN_EDIT_BROKER_CONTACT'
  | 'CAN_VIEW_DEALS'
  | 'CAN_CREATE_DEAL'
  | 'CAN_EDIT_DEAL'
  | 'CAN_DELETE_DEAL'
  | 'CAN_VIEW_REPORTS'
  | 'CAN_VIEW_LOGS'
  | 'CAN_VIEW_SETTINGS'
  | 'CAN_EDIT_SETTINGS'
  | 'CAN_MANAGE_ACCOUNTS'
  | 'CAN_RESET_PLATFORM'
  | 'CAN_MANAGE_PAYOUTS'
  | 'CAN_MANAGE_COMMISSION_RATES'
  | 'CAN_VIEW_SAEE_COMMISSION'
  | 'CAN_VIEW_TAX_INVOICE'
  | 'CAN_MANAGE_ROLES';

/** Backend roles as returned by GET /api/auth/me. */
export type BackendRole =
  | 'system_administrator'
  | 'operations_manager'
  | 'financial_officer'
  | 'support_agent'
  | 'user'
  | string;

export const ALL_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_CREATE_BROKER',
  'CAN_EDIT_BROKER',
  'CAN_EDIT_BROKER_CONTACT',
  'CAN_VIEW_DEALS',
  'CAN_CREATE_DEAL',
  'CAN_EDIT_DEAL',
  'CAN_DELETE_DEAL',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_LOGS',
  'CAN_VIEW_SETTINGS',
  'CAN_EDIT_SETTINGS',
  'CAN_MANAGE_ACCOUNTS',
  'CAN_RESET_PLATFORM',
  'CAN_MANAGE_PAYOUTS',
  'CAN_MANAGE_COMMISSION_RATES',
  'CAN_VIEW_SAEE_COMMISSION',
  'CAN_VIEW_TAX_INVOICE',
  'CAN_MANAGE_ROLES',
];

const SYSTEM_ADMIN_PERMISSIONS: Permission[] = ALL_PERMISSIONS;

const OPERATIONS_MANAGER_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_CREATE_BROKER',
  'CAN_EDIT_BROKER',
  'CAN_VIEW_DEALS',
  'CAN_CREATE_DEAL',
  'CAN_EDIT_DEAL',
  'CAN_DELETE_DEAL',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_LOGS',
  'CAN_VIEW_SETTINGS',
  'CAN_VIEW_TAX_INVOICE',
];

const FINANCIAL_OFFICER_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_VIEW_DEALS',
  'CAN_VIEW_REPORTS',
  'CAN_VIEW_SETTINGS',
  'CAN_MANAGE_PAYOUTS',
  'CAN_MANAGE_COMMISSION_RATES',
  'CAN_VIEW_SAEE_COMMISSION',
  'CAN_VIEW_TAX_INVOICE',
];

const SUPPORT_AGENT_PERMISSIONS: Permission[] = [
  'CAN_VIEW_OVERVIEW',
  'CAN_VIEW_BROKERS',
  'CAN_VIEW_DEALS',
  'CAN_VIEW_SETTINGS',
  'CAN_EDIT_BROKER_CONTACT',
];

const ROLE_PERMISSION_MAP: Record<AppRole, Permission[]> = {
  SYSTEM_ADMIN: SYSTEM_ADMIN_PERMISSIONS,
  OPERATIONS_MANAGER: OPERATIONS_MANAGER_PERMISSIONS,
  FINANCIAL_OFFICER: FINANCIAL_OFFICER_PERMISSIONS,
  SUPPORT_AGENT: SUPPORT_AGENT_PERMISSIONS,
};

const BACKEND_TO_APP_ROLE: Record<string, AppRole> = {
  system_administrator: 'SYSTEM_ADMIN',
  operations_manager: 'OPERATIONS_MANAGER',
  financial_officer: 'FINANCIAL_OFFICER',
  support_agent: 'SUPPORT_AGENT',
  // Legacy / test role — read-only access equivalent to support_agent.
  viewer: 'SUPPORT_AGENT',
};

/** Backend roles that are allowed into the admin dashboard (and must stay out of the broker app). */
export const DASHBOARD_ROLES = new Set([
  'system_administrator',
  'operations_manager',
  'financial_officer',
  'support_agent',
]);

/** Returns true if the backend role is an admin/dashboard role. */
export function isDashboardRole(backendRole: BackendRole | null | undefined): boolean {
  return DASHBOARD_ROLES.has(String(backendRole ?? '').trim().toLowerCase());
}

/**
 * Convert a backend role string to an app-level role.
 * Unknown / broker roles default to SUPPORT_AGENT (safest for admin UI).
 */
export function toAppRole(backendRole: BackendRole | null | undefined): AppRole {
  const normalized = String(backendRole ?? '').trim().toLowerCase();
  return BACKEND_TO_APP_ROLE[normalized] ?? 'SUPPORT_AGENT';
}

/** Return the full permission set for an app-level role. */
export function getPermissions(role: AppRole): readonly Permission[] {
  return ROLE_PERMISSION_MAP[role] ?? SUPPORT_AGENT_PERMISSIONS;
}

/** Check whether a role holds a specific permission. */
export function hasPermission(role: AppRole, permission: Permission): boolean {
  return getPermissions(role).includes(permission);
}

/** Check whether a backend role string directly holds a permission (convenience). */
export function backendRoleHasPermission(
  backendRole: BackendRole | null | undefined,
  permission: Permission,
): boolean {
  return hasPermission(toAppRole(backendRole), permission);
}

function normalizeBackofficePath(pathname: string): string {
  const p = pathname.replace(/\/+$/, '') || '/';
  return p === '' ? '/' : p;
}

/**
 * Minimum permission required for the current backoffice URL (path + optional `?tab=` on Settings).
 * Used by AdminRouteGuard for deep links and live permission revocations.
 */
export function getRequiredPermissionForPath(pathname: string, search: string): Permission | undefined {
  const path = normalizeBackofficePath(pathname);
  const base = normalizeBackofficePath(BACKOFFICE_BASE);

  if (path === base) {
    return 'CAN_VIEW_OVERVIEW';
  }
  if (path === `${base}/brokers` || path.startsWith(`${base}/brokers/`)) {
    if (path === `${base}/brokers/new`) {
      return 'CAN_CREATE_BROKER';
    }
    return 'CAN_VIEW_BROKERS';
  }
  if (path === `${base}/deals/new` || path === `${base}/brokerage/new`) {
    return 'CAN_CREATE_DEAL';
  }
  if (
    path === `${base}/deals` ||
    path.startsWith(`${base}/deals/`) ||
    path === `${base}/brokerage` ||
    path.startsWith(`${base}/brokerage/`)
  ) {
    return 'CAN_VIEW_DEALS';
  }
  if (path === `${base}/commission-reports`) {
    return 'CAN_VIEW_REPORTS';
  }
  if (path === `${base}/system-logs`) {
    return 'CAN_VIEW_LOGS';
  }
  if (path === `${base}/settings`) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const tab = (params.get('tab') || '').trim().toLowerCase();
    if (tab === 'accounts') {
      return 'CAN_MANAGE_ACCOUNTS';
    }
    if (tab === 'roles-permissions' || tab === 'roles') {
      return 'CAN_MANAGE_ROLES';
    }
    return 'CAN_VIEW_SETTINGS';
  }
  if (path === `${base}/settings/accounts/new`) {
    return 'CAN_MANAGE_ACCOUNTS';
  }
  if (path === `${base}/settings/roles-permissions`) {
    return 'CAN_MANAGE_ROLES';
  }
  if (path === `${base}/access-denied`) {
    return undefined;
  }
  return undefined;
}

/** Nav items that should be hidden when the user lacks the permission. */
export const NAV_PERMISSION_MAP: Record<string, Permission | undefined> = {
  [BACKOFFICE_BASE]: 'CAN_VIEW_OVERVIEW',
  [`${BACKOFFICE_BASE}/brokers`]: 'CAN_VIEW_BROKERS',
  [`${BACKOFFICE_BASE}/deals`]: 'CAN_VIEW_DEALS',
  [`${BACKOFFICE_BASE}/brokerage`]: 'CAN_VIEW_DEALS',
  [`${BACKOFFICE_BASE}/commission-reports`]: 'CAN_VIEW_REPORTS',
  [`${BACKOFFICE_BASE}/system-logs`]: 'CAN_VIEW_LOGS',
  [`${BACKOFFICE_BASE}/settings`]: 'CAN_VIEW_SETTINGS',
};
